/**
 * Colour tokens generated from one base colour: white, an ink, the base colour with its tints
 * and shades, and status colours harmonised to it. Coefficients are fitted to the reference skins.
 * @since NEXT_VERSION
 */
import chroma from "chroma-js";

const WHITE = "#FFFFFF";
const BLACK = "#000000";

// @since NEXT_VERSION
export const DEFAULT_BASE_COLOR = "#117169";

// @since NEXT_VERSION
// Lagoon, the default, comes first. The blue-greens sit close to the InOpTra logo's teal (#20A2B0).
export const BASE_COLOR_PRESETS = [
  { id: "lagoon", name: "Lagoon", color: "#117169" },
  { id: "ocean-teal", name: "Ocean teal", color: "#0F6E8C" },
  { id: "plum", name: "Plum", color: "#7E3A9E" },
];

// OKLCH hue and chroma each status colour starts from before it leans toward the base colour.
const STATUS_SEEDS = {
  success: [150, 0.13],
  warning: [65, 0.14],
  error: [27, 0.18],
};

const MIN_TEXT_CONTRAST = 4.5;
const MIN_ACCENT_LIGHTNESS = 0.3;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function toOklch(color) {
  const [l, c, h] = chroma(color).oklch();
  return [l, c, Number.isNaN(h) ? 0 : h];
}

// Lowers chroma until the colour fits sRGB, so out-of-gamut tints keep their hue.
function fromOklch(l, c, h) {
  const lightness = clamp(l, 0, 1);
  const hue = ((h % 360) + 360) % 360;
  if (!chroma.oklch(lightness, c, hue).clipped()) return chroma.oklch(lightness, c, hue).hex().toUpperCase();
  let low = 0;
  let high = c;
  for (let i = 0; i < 16; i += 1) {
    const mid = (low + high) / 2;
    if (chroma.oklch(lightness, mid, hue).clipped()) high = mid;
    else low = mid;
  }
  return chroma.oklch(lightness, low, hue).hex().toUpperCase();
}

const mixRgb = (from, to, amount) => chroma.mix(from, to, amount, "rgb").hex().toUpperCase();
const scaleRgb = (color, factor) =>
  chroma(chroma(color).rgb().map((v) => clamp(Math.round(v * factor), 0, 255))).hex().toUpperCase();
const rgbTriplet = (color) => chroma(color).rgb().join(",");

function darkenForWhiteText(color) {
  if (chroma.contrast(color, WHITE) >= MIN_TEXT_CONTRAST) return color;
  const [l, c, h] = toOklch(color);
  let low = 0;
  let high = l;
  for (let i = 0; i < 20; i += 1) {
    const mid = (low + high) / 2;
    if (chroma.contrast(fromOklch(mid, c, h), WHITE) >= MIN_TEXT_CONTRAST) low = mid;
    else high = mid;
  }
  return fromOklch(low, c, h);
}

/**
 * The light-theme accent for a base colour. A colour that carries white text is kept as picked;
 * a pale one is darkened just enough, and a near-black one is lifted so it stays apart from the ink.
 * @since NEXT_VERSION
 */
export function resolveAccent(baseColor) {
  const picked = chroma.valid(baseColor) ? chroma(baseColor).hex("rgb").toUpperCase() : DEFAULT_BASE_COLOR;
  const [l, c, h] = toOklch(picked);
  const lifted = l < MIN_ACCENT_LIGHTNESS ? fromOklch(MIN_ACCENT_LIGHTNESS, c, h) : picked;
  return darkenForWhiteText(lifted);
}

// One colour's tones in both themes; dark mode uses the lighter tints as its main colour.
function family(main) {
  const [l, c, h] = toOklch(main);
  const tone = clamp(l, 0.4, 0.58);
  const calm = Math.min(c, 0.2);
  const at = (base, slope, chromaFactor, hueShift) =>
    fromOklch(base + slope * tone, chromaFactor * calm, h + hueShift);
  const start = [tone + 0.14, c * 0.97];
  const end = [0.965, 0.015];
  const tint = (t) => fromOklch(start[0] + (end[0] - start[0]) * t, start[1] + (end[1] - start[1]) * t, h + 3);
  return {
    main,
    hover: mixRgb(main, BLACK, 0.19),
    strong: mixRgb(main, BLACK, 0.4),
    focus: scaleRgb(main, 1.185),
    soft: at(0.934, 0.071, 0.084, 4.3),
    soft2: at(0.847, 0.173, 0.179, 6.9),
    tints: [tint(0), tint(1 / 3), tint(2 / 3)],
    darkSoft: at(0.115, 0.28, 0.289, 3.2),
    darkSoft2: at(0.073, 0.448, 0.419, 2.6),
    darkHero: at(0.124, 0.279, 0.312, 4),
  };
}

// Status hues share the base colour's tone; a hue near the base hue is pushed away so the two
// stay distinguishable, and a distant one leans slightly toward it.
function statusFamilies(accent) {
  const [l, c, h] = toOklch(accent);
  const tone = clamp(l, 0.45, 0.55);
  const vividness = 0.85 + 0.3 * clamp(c / 0.18, 0, 1);
  return Object.fromEntries(
    Object.entries(STATUS_SEEDS).map(([name, [hue, seedChroma]]) => {
      const delta = ((h - hue + 540) % 360) - 180;
      const distance = Math.abs(delta);
      let shift = 0;
      if (c >= 0.02 && distance < 40) shift = -Math.sign(delta) * (40 - distance) * 0.5;
      else if (c >= 0.02) shift = Math.sign(delta) * Math.min(distance * 0.15, 6);
      return [name, family(darkenForWhiteText(fromOklch(tone, seedChroma * vividness, hue + shift)))];
    })
  );
}

function statusTokens(families, mode, ink) {
  return Object.entries(families).reduce((tokens, [name, f]) => {
    const dark = mode === "dark";
    return {
      ...tokens,
      [name]: dark ? f.tints[1] : f.main,
      [`${name}-hover`]: dark ? f.tints[2] : f.hover,
      [`${name}-strong`]: dark ? f.tints[2] : f.strong,
      [`${name}-soft`]: dark ? f.darkSoft : f.soft,
      [`${name}-soft-2`]: dark ? f.darkSoft2 : f.soft2,
      [`${name}-tint`]: dark ? f.tints[2] : f.tints[0],
      [`on-${name}`]: dark ? ink : WHITE,
    };
  }, {});
}

const SHADOWS = {
  light: (rgb, accent) => ({
    "shadow-1": `0 1px 2px rgba(${rgb},.06)`,
    "shadow-2": `0 1px 2px rgba(${rgb},.05), 0 10px 28px -14px rgba(${rgb},.22)`,
    "shadow-3": `0 2px 6px rgba(${rgb},.08), 0 32px 70px -24px rgba(${rgb},.45)`,
    "shadow-accent": `0 1px 0 rgba(255,255,255,.18) inset, 0 8px 18px -8px ${accent}`,
    hairline: "inset 0 1px 0 rgba(255,255,255,0)",
  }),
  dark: (rgb, accent) => ({
    "shadow-1": "0 1px 2px rgba(0,0,0,.5)",
    "shadow-2": "0 1px 0 rgba(255,255,255,.04) inset, 0 14px 34px -16px rgba(0,0,0,.85)",
    "shadow-3": "0 1px 0 rgba(255,255,255,.06) inset, 0 40px 90px -30px rgba(0,0,0,.95)",
    "shadow-accent": `0 1px 0 rgba(255,255,255,.25) inset, 0 10px 22px -10px ${accent}`,
    hairline: "inset 0 1px 0 rgba(255,255,255,.05)",
  }),
};

/**
 * Light and dark tokens for a base colour, named as in the reference design (accent, ink, c1..c5).
 * `adjusted` is true when the base colour had to change to keep text readable.
 * @since NEXT_VERSION
 */
export function buildTokens(baseColor = DEFAULT_BASE_COLOR) {
  const picked = chroma.valid(baseColor) ? chroma(baseColor).hex("rgb").toUpperCase() : DEFAULT_BASE_COLOR;
  const accent = resolveAccent(picked);
  const [l, c, h] = toOklch(accent);
  const tone = clamp(l, 0.4, 0.58);
  const calm = Math.min(c, 0.2);
  const at = (base, slope, chromaFactor, hueShift) =>
    fromOklch(base + slope * tone, chromaFactor * calm, h + hueShift);
  const ink = fromOklch(0.062 + 0.227 * tone, Math.min(0.115 * c, 0.022), h + 6.4);
  const grey = (amount) => mixRgb(ink, WHITE, amount);
  const a = family(accent);
  const status = statusFamilies(accent);

  const light = {
    bg: at(0.966, 0.037, 0.044, 0.3),
    surface: WHITE,
    "surface-2": at(0.953, 0.05, 0.06, 1.8),
    "surface-3": at(0.908, 0.095, 0.119, 5.2),
    border: grey(0.89),
    "border-strong": grey(0.78),
    text: ink,
    "text-2": grey(0.3),
    "text-3": grey(0.38),
    "field-border": grey(0.54),
    accent,
    "accent-hover": a.hover,
    "accent-strong": a.strong,
    "accent-soft": a.soft,
    "accent-soft-2": a.soft2,
    "on-accent": WHITE,
    focus: a.focus,
    ink,
    "on-ink": WHITE,
    "on-ink-2": grey(0.72),
    "on-ink-3": grey(0.56),
    "ink-raise": grey(0.08),
    "ink-line": grey(0.14),
    "ink-accent": a.tints[1],
    "hero-bg": ink,
    c1: a.strong,
    c2: accent,
    c3: a.tints[0],
    c4: a.tints[1],
    c5: a.tints[2],
    scrim: `rgba(${rgbTriplet(ink)},.52)`,
    "sh-rgb": rgbTriplet(ink),
    ...statusTokens(status, "light", ink),
    ...SHADOWS.light(rgbTriplet(ink), accent),
  };

  const dark = {
    bg: at(0.085, 0.11, 0.082, 13.1),
    surface: at(0.168, 0.07, 0.09, 3.7),
    "surface-2": at(0.182, 0.099, 0.106, 2),
    "surface-3": at(0.217, 0.104, 0.127, 3.1),
    border: "#2C2C2C",
    "border-strong": "#414141",
    text: at(0.95, 0.064, 0.046, 12.9),
    "text-2": "#B8B8B8",
    "text-3": "#9B9B9B",
    "field-border": "#676767",
    accent: a.tints[1],
    "accent-hover": a.tints[2],
    "accent-strong": a.tints[2],
    "accent-soft": a.darkSoft,
    "accent-soft-2": a.darkSoft2,
    "on-accent": ink,
    focus: a.tints[1],
    ink: at(0.083, 0.066, 0.082, 7.5),
    "on-ink": WHITE,
    "on-ink-2": "#BABABA",
    "on-ink-3": "#939393",
    "ink-raise": "#1B1B1B",
    "ink-line": "#272727",
    "ink-accent": a.tints[1],
    "hero-bg": a.darkHero,
    c1: a.tints[2],
    c2: a.tints[1],
    c3: a.tints[0],
    c4: accent,
    c5: a.strong,
    scrim: "rgba(0,0,0,.66)",
    "sh-rgb": "0,0,0",
    ...statusTokens(status, "dark", ink),
    ...SHADOWS.dark("0,0,0", a.tints[1]),
  };

  return { base: picked, accent, adjusted: accent !== picked, light, dark };
}

/**
 * CSS custom properties (`--accent`, `--surface-2`, ...) for one theme's tokens.
 * @since NEXT_VERSION
 */
export function toCssVariables(modeTokens) {
  return Object.fromEntries(Object.entries(modeTokens).map(([name, value]) => [`--${name}`, value]));
}
